import { type ChoixResultat, ONGLET_ORIGINE } from '@/lib/params-recherche';
import { cn } from '@/lib/utils';
import { Icon } from '@pms-digital/design-system';
import type { NoyauCode, NoyauEnCours } from '@rosetta/shared';
import { type KeyboardEvent, useRef } from 'react';
import { LienNoyauEnCours } from './lien-noyau-en-cours';
import { RondChargement } from './noyau-en-cours';
import { infobulleNoyau, styleNoyau } from './noyaux-affichage';
import { PastilleNoyau } from './pastille-noyau';

/** `id` of the panel the tabs control: the card of the noyau shown, the only one in the page. */
export const ID_PANNEAU_RESULTAT = 'panneau-resultat';

/** `id` of a tab (a noyau, or the original text, #38), for the `aria-labelledby` of the panel. */
export const idOngletResultat = (choix: ChoixResultat): string => `onglet-resultat-${choix}`;

/** The name of the first tab, which is also the name of its card (#38). */
export const LIBELLE_TEXTE_ORIGINE = "Texte d'origine";

interface OngletsNoyauxProps {
  /** The noyaux chosen at the input, in charter order: one tab each. */
  noyaux: readonly NoyauCode[];
  /** The tab shown: a noyau, or `origine` (#38). */
  actif: ChoixResultat;
  onChange: (choix: ChoixResultat) => void;
  /**
   * The text as it was entered is known (#38): "Texte d'origine" is the first tab, before the
   * noyaux. It is never the tab shown by default, `actif` says when it is.
   */
  avecOrigine?: boolean;
  /** The noyaux whose translation did not succeed (a partial translation, #13): marked on their tab. */
  enEchec?: readonly NoyauCode[];
  /**
   * The noyaux another launch of the document is translating (#36), in charter order: after the
   * tabs, each a link with a ring to the waiting screen of its launch.
   */
  enCours?: readonly NoyauEnCours[];
}

/**
 * Tab bar of the result: round dot and code, one tab per chosen noyau. The shown
 * tab is underlined 3 px in the colour of its noyau, the others stay `gray-700`,
 * never greyed out. Changing tab changes the card, never launches a translation. The tabs
 * control one panel, the card (`ID_PANNEAU_RESULTAT`), which the result screen draws. On a
 * partial translation (#13) the tab of a noyau that did not succeed carries the `error` icon
 * after its code, and its name says so.
 *
 * "Texte d'origine" (#38) is the first tab, before the noyaux, when the text as it was entered is
 * known: no dot (it is no noyau) and no icon, its name alone, a thin rule that sets it
 * apart from the translations, and a black underline when shown. It is a tab like the others
 * (same panel, same roving focus): the arrows, `Home` and `End` go through it.
 *
 * A noyau being translated by another launch of the document (#36) is not a tab (it has no card to
 * show, and a tab never leaves the page): it is a link, in a list of its own on the same row,
 * after the tabs, with a turning ring, named "<noyau>, en cours de traduction". It goes to the
 * waiting screen of that launch.
 */
export function OngletsNoyaux({
  noyaux,
  actif,
  onChange,
  avecOrigine = false,
  enEchec = [],
  enCours = [],
}: OngletsNoyauxProps) {
  const refs = useRef(new Map<ChoixResultat, HTMLButtonElement>());
  // The tabs in the order of the row: the original text, then the noyaux.
  const choix: readonly ChoixResultat[] = avecOrigine ? [ONGLET_ORIGINE, ...noyaux] : noyaux;

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (choix.length === 0) return;
    const index = choix.indexOf(actif);
    let suivant: ChoixResultat | undefined;
    if (event.key === 'ArrowRight') suivant = choix[(index + 1) % choix.length];
    else if (event.key === 'ArrowLeft') suivant = choix[(index - 1 + choix.length) % choix.length];
    else if (event.key === 'Home') suivant = choix[0];
    else if (event.key === 'End') suivant = choix[choix.length - 1];
    else return;
    event.preventDefault();
    if (suivant === undefined) return;
    onChange(suivant);
    refs.current.get(suivant)?.focus();
  }

  function enregistrer(code: ChoixResultat) {
    return (element: HTMLButtonElement | null) => {
      if (element) refs.current.set(code, element);
      else refs.current.delete(code);
    };
  }

  return (
    <div className="flex flex-wrap items-stretch border-gray-200 border-b">
      <div
        role="tablist"
        aria-label="noyaux choisis"
        onKeyDown={onKeyDown}
        className="flex flex-wrap items-stretch"
      >
        {avecOrigine ? (
          <button
            ref={enregistrer(ONGLET_ORIGINE)}
            type="button"
            role="tab"
            id={idOngletResultat(ONGLET_ORIGINE)}
            aria-selected={actif === ONGLET_ORIGINE}
            aria-controls={ID_PANNEAU_RESULTAT}
            tabIndex={actif === ONGLET_ORIGINE ? 0 : -1}
            onClick={() => onChange(ONGLET_ORIGINE)}
            className={cn(
              'after:-right-1 relative mr-2 inline-flex h-12 items-center gap-2 rounded-t-md border-b-[3px] bg-clip-padding px-4 transition-colors after:absolute after:top-4 after:bottom-4 after:w-px after:border-l after:border-gray-300 hover:bg-gray-100',
              actif === ONGLET_ORIGINE ? 'border-black' : 'border-transparent',
            )}
          >
            <span
              className={cn(
                'whitespace-nowrap font-bold text-small leading-none',
                actif === ONGLET_ORIGINE ? 'text-black' : 'text-gray-700',
              )}
            >
              {LIBELLE_TEXTE_ORIGINE}
            </span>
          </button>
        ) : null}
        {noyaux.map((code) => {
          const selectionne = code === actif;
          const echoue = enEchec.includes(code);
          const nom = echoue ? `${infobulleNoyau(code)}, n'a pas abouti` : infobulleNoyau(code);
          return (
            <button
              key={code}
              ref={enregistrer(code)}
              type="button"
              role="tab"
              id={idOngletResultat(code)}
              aria-selected={selectionne}
              aria-controls={ID_PANNEAU_RESULTAT}
              aria-label={nom}
              title={nom}
              tabIndex={selectionne ? 0 : -1}
              onClick={() => onChange(code)}
              className={cn(
                'inline-flex h-12 items-center gap-2 rounded-t-md border-b-[3px] bg-clip-padding px-4 transition-colors hover:bg-gray-100',
                selectionne ? styleNoyau(code).soulignement : 'border-transparent',
              )}
            >
              <PastilleNoyau noyau={code} />
              <span
                className={cn(
                  'font-bold text-small leading-none tracking-[1px]',
                  selectionne ? 'text-black' : 'text-gray-700',
                )}
              >
                {code}
              </span>
              {echoue ? (
                <span className="inline-flex shrink-0 text-rouge-deep">
                  <Icon name="error" size={16} />
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
      {enCours.length === 0 ? null : (
        <ul
          aria-label="noyaux en cours de traduction"
          className="m-0 flex flex-wrap list-none items-stretch p-0"
        >
          {enCours.map(({ noyau, lancement }) => (
            <li key={noyau} className="flex">
              <LienNoyauEnCours
                noyau={noyau}
                lancement={lancement}
                className="inline-flex h-12 items-center gap-2 rounded-t-md border-transparent border-b-[3px] bg-clip-padding px-4 text-gray-700 transition-colors hover:bg-gray-100 hover:text-gray-700"
              >
                <PastilleNoyau noyau={noyau} />
                <span className="font-bold text-small leading-none tracking-[1px]">{noyau}</span>
                <RondChargement />
              </LienNoyauEnCours>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
